import { useEffect, useMemo, useState } from 'react';
import {
	Autocomplete,
	Box,
	Button,
	IconButton,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	MenuItem,
	Paper,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import axios from 'axios';
import { FaTrash } from 'react-icons/fa';
import { Title, useNotify } from 'react-admin';
import constants from '../../constants';

// GH-724: every role that grants admin access is listed and assignable here (SOC2 access review) —
// the narrow roles used to live only in the DB. Keep in sync with MANAGED_ROLES in the backend.
type Role = 'admin' | 'superadmin' | 'editor' | 'business-admin' | 'group-sessions-viewer' | 'expert-manager' | 'user';

type PrivilegedUser = {
	id: string;
	_id: string;
	email: string;
	first_name?: string;
	last_name?: string;
	role: Role;
	active?: boolean;
	createdAt?: string;
	updatedAt?: string;
};

const ROLE_LABEL: Record<Role, string> = {
	superadmin: 'Super Admin',
	admin: 'Admin',
	editor: 'Editor',
	'business-admin': 'מנהל/ת עסקי/ת (חברות)',
	'group-sessions-viewer': 'צפייה בהרצאות בלבד',
	'expert-manager': 'תמיכה טכנית',
	user: 'משתמש רגיל',
};

const ROLE_COLOR: Record<Role, 'default' | 'primary' | 'secondary' | 'warning' | 'error' | 'info'> = {
	superadmin: 'error',
	admin: 'warning',
	editor: 'info',
	'business-admin': 'secondary',
	'group-sessions-viewer': 'primary',
	'expert-manager': 'primary',
	user: 'default',
};

const ASSIGNABLE_ROLES: Role[] = ['superadmin', 'admin', 'editor', 'business-admin', 'group-sessions-viewer', 'expert-manager', 'user'];

const PermissionsList = () => {
	const notify = useNotify();
	const [users, setUsers] = useState<PrivilegedUser[]>([]);
	const [loading, setLoading] = useState(false);
	const [pendingChange, setPendingChange] = useState<{
		user: PrivilegedUser;
		newRole: Role;
	} | null>(null);
	const [pendingActiveChange, setPendingActiveChange] = useState<{
		user: PrivilegedUser;
		newActive: boolean;
	} | null>(null);
	const [saving, setSaving] = useState(false);

	const [pendingDelete, setPendingDelete] = useState<PrivilegedUser | null>(null);

	// Add-user-by-search state
	const [searchQuery, setSearchQuery] = useState('');
	const [searchOptions, setSearchOptions] = useState<PrivilegedUser[]>([]);
	const [searchLoading, setSearchLoading] = useState(false);
	const [selectedCandidate, setSelectedCandidate] = useState<PrivilegedUser | null>(null);
	const [candidateRole, setCandidateRole] = useState<Role>('admin');

	const currentUserId = useMemo(() => {
		try {
			const token = localStorage.getItem('token');
			if (!token) return undefined;
			const payload = JSON.parse(atob(token.split('.')[1] || ''));
			return payload?._id as string | undefined;
		} catch {
			return undefined;
		}
	}, []);

	const fetchUsers = async () => {
		setLoading(true);
		try {
			const token = localStorage.getItem('token');
			const { data } = await axios.get<PrivilegedUser[]>(
				`${constants.API_URL}admin/permissions`,
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			setUsers(data);
		} catch (err: any) {
			notify(err?.response?.data?.message || 'שגיאה בטעינת הרשאות', { type: 'error' });
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchUsers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Debounced server-side member search.
	useEffect(() => {
		const q = searchQuery.trim();
		if (q.length < 2) {
			setSearchOptions([]);
			setSearchLoading(false);
			return;
		}
		setSearchLoading(true);
		const handle = setTimeout(async () => {
			try {
				const token = localStorage.getItem('token');
				const { data } = await axios.get<PrivilegedUser[]>(
					`${constants.API_URL}admin/permissions/search`,
					{
						params: { q },
						headers: { Authorization: `Bearer ${token}` },
					},
				);
				setSearchOptions(data);
			} catch (err) {
				setSearchOptions([]);
			} finally {
				setSearchLoading(false);
			}
		}, 300);
		return () => clearTimeout(handle);
	}, [searchQuery]);

	const grantCandidateRole = async () => {
		if (!selectedCandidate) return;
		setSaving(true);
		try {
			const token = localStorage.getItem('token');
			await axios.put(
				`${constants.API_URL}admin/permissions/${selectedCandidate._id}`,
				{ role: candidateRole },
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			notify(`התפקיד ${ROLE_LABEL[candidateRole]} הוענק ל-${selectedCandidate.email}`, {
				type: 'success',
			});
			setSelectedCandidate(null);
			setSearchQuery('');
			setSearchOptions([]);
			setCandidateRole('admin');
			await fetchUsers();
		} catch (err: any) {
			notify(err?.response?.data?.message || 'שגיאה במתן ההרשאה', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	const confirmDelete = async () => {
		if (!pendingDelete) return;
		setSaving(true);
		try {
			const token = localStorage.getItem('token');
			await axios.delete(`${constants.API_URL}admin/permissions/${pendingDelete._id}`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			notify('המשתמש נמחק ונרשם ב-audit log', { type: 'success' });
			setPendingDelete(null);
			await fetchUsers();
		} catch (err: any) {
			notify(err?.response?.data?.message || 'שגיאה במחיקה', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	const confirmActiveChange = async () => {
		if (!pendingActiveChange) return;
		setSaving(true);
		try {
			const token = localStorage.getItem('token');
			await axios.put(
				`${constants.API_URL}admin/permissions/${pendingActiveChange.user._id}/active`,
				{ active: pendingActiveChange.newActive },
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			notify('הסטטוס עודכן ונשמר ב-audit log', { type: 'success' });
			setPendingActiveChange(null);
			await fetchUsers();
		} catch (err: any) {
			notify(err?.response?.data?.message || 'שגיאה בעדכון הסטטוס', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	const confirmRoleChange = async () => {
		if (!pendingChange) return;
		setSaving(true);
		try {
			const token = localStorage.getItem('token');
			await axios.put(
				`${constants.API_URL}admin/permissions/${pendingChange.user._id}`,
				{ role: pendingChange.newRole },
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			notify('התפקיד עודכן ונשמר ב-audit log', { type: 'success' });
			setPendingChange(null);
			await fetchUsers();
		} catch (err: any) {
			notify(err?.response?.data?.message || 'שגיאה בעדכון התפקיד', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	return (
		<Box sx={{ padding: '16px' }}>
			<Title title="הרשאות" />
			<Typography variant="h5" sx={{ mb: 1 }}>
				ניהול הרשאות אדמין
			</Typography>
			<Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
				רשימת המשתמשים בעלי הרשאות מורחבות (Admin / Super Admin / Editor). שינוי תפקיד
				נרשם ב-audit log עם המבצע, היעד וההפרש.
			</Typography>
			<Paper sx={{ p: 2, mb: 2 }}>
				<Typography variant="subtitle1" sx={{ mb: 1 }}>
					הענקת הרשאה למשתמש קיים
				</Typography>
				<Autocomplete
					options={searchOptions}
					value={null}
					inputValue={searchQuery}
					onInputChange={(_, val) => setSearchQuery(val)}
					onChange={(_, val) => {
						if (val) {
							setSelectedCandidate(val);
							setCandidateRole(
								val.role && ASSIGNABLE_ROLES.includes(val.role) && val.role !== 'user'
									? val.role
									: 'admin',
							);
						}
					}}
					getOptionLabel={(opt) =>
						opt
							? `${opt.email}${[opt.first_name, opt.last_name].filter(Boolean).length ? ` — ${[opt.first_name, opt.last_name].filter(Boolean).join(' ')}` : ''}`
							: ''
					}
					isOptionEqualToValue={(a, b) => a._id === b._id}
					loading={searchLoading}
					filterOptions={(x) => x}
					noOptionsText={searchQuery.trim().length < 2 ? 'הקלד לפחות 2 תווים' : 'לא נמצאו תוצאות'}
					renderOption={(props, opt) => (
						<li {...props} key={opt._id}>
							<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
									<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
										{opt.email}
									</Typography>
									{opt.role && opt.role !== 'user' && (
										<Chip
											label={ROLE_LABEL[opt.role] || opt.role}
											color={ROLE_COLOR[opt.role] || 'default'}
											size="small"
										/>
									)}
								</Box>
								{[opt.first_name, opt.last_name].filter(Boolean).length > 0 && (
									<Typography variant="caption" color="text.secondary">
										{[opt.first_name, opt.last_name].filter(Boolean).join(' ')}
									</Typography>
								)}
							</Box>
						</li>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="חפש משתמש (אימייל / שם)"
							placeholder="לפחות 2 תווים..."
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{searchLoading ? (
											<CircularProgress color="inherit" size={18} />
										) : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>
			</Paper>

			<Paper>
				{loading ? (
					<Box sx={{ p: 4, textAlign: 'center' }}>
						<CircularProgress />
					</Box>
				) : (
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>אימייל</TableCell>
									<TableCell>שם</TableCell>
									<TableCell>תפקיד נוכחי</TableCell>
									<TableCell align="center">פעיל</TableCell>
									<TableCell align="center">שנה תפקיד</TableCell>
									<TableCell align="center">מחק</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{users.map((u) => {
									const fullName =
										[u.first_name, u.last_name].filter(Boolean).join(' ') || '—';
									const isSelf = currentUserId && u._id === currentUserId;
									return (
										<TableRow key={u._id}>
											<TableCell sx={{ fontFamily: 'monospace' }}>{u.email}</TableCell>
											<TableCell>{fullName}</TableCell>
											<TableCell>
												<Chip
													label={ROLE_LABEL[u.role] || u.role}
													color={ROLE_COLOR[u.role] || 'default'}
													size="small"
												/>
											</TableCell>
											<TableCell align="center">
												{isSelf ? (
													<Chip
														label={u.active === false ? 'לא פעיל' : 'פעיל'}
														color={u.active === false ? 'default' : 'success'}
														size="small"
														variant={u.active === false ? 'outlined' : 'filled'}
													/>
												) : (
													<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
														<Switch
															checked={u.active !== false}
															onChange={(_, checked) =>
																setPendingActiveChange({ user: u, newActive: checked })
															}
															color="success"
														/>
														<Typography
															variant="caption"
															sx={{ minWidth: 50, textAlign: 'right', color: u.active === false ? '#999' : '#2e7d32' }}
														>
															{u.active === false ? 'לא פעיל' : 'פעיל'}
														</Typography>
													</Box>
												)}
											</TableCell>
											<TableCell align="center">
												{isSelf ? (
													<Typography variant="caption" color="text.secondary">
														(זה אתה)
													</Typography>
												) : (
													<Select
														size="small"
														value={u.role}
														onChange={(e) =>
															setPendingChange({
																user: u,
																newRole: e.target.value as Role,
															})
														}
														sx={{ minWidth: 160 }}
													>
														{ASSIGNABLE_ROLES.map((r) => (
															<MenuItem key={r} value={r}>
																{ROLE_LABEL[r]}
															</MenuItem>
														))}
													</Select>
												)}
											</TableCell>
											<TableCell align="center">
												{!isSelf && (
													<IconButton
														size="small"
														color="error"
														onClick={() => setPendingDelete(u)}
														title="מחק משתמש"
													>
														<FaTrash />
													</IconButton>
												)}
											</TableCell>
										</TableRow>
									);
								})}
								{!users.length && (
									<TableRow>
										<TableCell colSpan={6} align="center" sx={{ color: '#999' }}>
											אין משתמשים עם הרשאות מורחבות
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Paper>

			<Dialog open={!!pendingDelete} onClose={() => !saving && setPendingDelete(null)}>
				<DialogTitle>מחיקת משתמש</DialogTitle>
				<DialogContent>
					{pendingDelete && (
						<DialogContentText>
							למחוק את <b>{pendingDelete.email}</b>?
							<br />
							<br />
							הפעולה תבצע את אותה לוגיקת מחיקה כמו במקומות אחרים במערכת:
							<br />
							• פרטים אישיים (שם, אימייל, טלפון) יוחלפו ב-hash bcrypt
							<br />
							• המשתמש יוגדר כלא פעיל
							<br />
							• Refresh tokens ו-push tokens יימחקו
							<br />
							• מנוי ActiveTrail יוסר
							<br />
							<br />
							הפעולה תיכתב ב-audit log עם זמן, מבצע ויעד.
						</DialogContentText>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setPendingDelete(null)} disabled={saving}>
						ביטול
					</Button>
					<Button onClick={confirmDelete} disabled={saving} variant="contained" color="error">
						{saving ? 'מוחק...' : 'מחק'}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={!!selectedCandidate}
				onClose={() => !saving && setSelectedCandidate(null)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>הענקת הרשאה</DialogTitle>
				<DialogContent>
					{selectedCandidate && (
						<>
							<DialogContentText sx={{ mb: 2 }}>
								להעניק הרשאה ל-<b>{selectedCandidate.email}</b>
								{selectedCandidate.role && selectedCandidate.role !== 'user' && (
									<>
										<br />
										<small>
											תפקיד נוכחי:{' '}
											<b>{ROLE_LABEL[selectedCandidate.role] || selectedCandidate.role}</b>
										</small>
									</>
								)}
							</DialogContentText>
							<Select
								fullWidth
								size="small"
								value={candidateRole}
								onChange={(e) => setCandidateRole(e.target.value as Role)}
							>
								{ASSIGNABLE_ROLES.filter((r) => r !== 'user').map((r) => (
									<MenuItem key={r} value={r}>
										{ROLE_LABEL[r]}
									</MenuItem>
								))}
							</Select>
							<Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
								הפעולה תיכתב ב-audit log עם זמן, מבצע ויעד.
							</Typography>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setSelectedCandidate(null)} disabled={saving}>
						ביטול
					</Button>
					<Button
						onClick={grantCandidateRole}
						disabled={saving}
						variant="contained"
						color="warning"
					>
						{saving ? 'שומר...' : 'הענק הרשאה'}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={!!pendingActiveChange} onClose={() => !saving && setPendingActiveChange(null)}>
				<DialogTitle>
					{pendingActiveChange?.newActive ? 'הפעלת משתמש' : 'השבתת משתמש'}
				</DialogTitle>
				<DialogContent>
					{pendingActiveChange && (
						<DialogContentText>
							{pendingActiveChange.newActive ? 'להפעיל את' : 'להשבית את'}{' '}
							<b>{pendingActiveChange.user.email}</b>?
							<br />
							<br />
							{!pendingActiveChange.newActive && (
								<>משתמש לא פעיל לא יוכל להתחבר למערכת.<br /><br /></>
							)}
							הפעולה תיכתב ב-audit log עם זמן, מבצע ויעד.
						</DialogContentText>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setPendingActiveChange(null)} disabled={saving}>
						ביטול
					</Button>
					<Button
						onClick={confirmActiveChange}
						disabled={saving}
						variant="contained"
						color={pendingActiveChange?.newActive ? 'success' : 'warning'}
					>
						{saving ? 'שומר...' : 'אשר ושמור'}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={!!pendingChange} onClose={() => !saving && setPendingChange(null)}>
				<DialogTitle>אישור שינוי תפקיד</DialogTitle>
				<DialogContent>
					{pendingChange && (
						<DialogContentText>
							לשנות את התפקיד של <b>{pendingChange.user.email}</b> מ-
							<b>{ROLE_LABEL[pendingChange.user.role]}</b> ל-
							<b>{ROLE_LABEL[pendingChange.newRole]}</b>?
							<br />
							<br />
							הפעולה תיכתב ב-audit log עם זמן, מבצע ויעד.
						</DialogContentText>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setPendingChange(null)} disabled={saving}>
						ביטול
					</Button>
					<Button onClick={confirmRoleChange} disabled={saving} variant="contained" color="warning">
						{saving ? 'שומר...' : 'אשר ושמור'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default PermissionsList;
